import { Box, Button, Container, Grid, Typography } from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { BlogArticleCard } from "components/blog-cards/BlogArticleCard";
import CloseIcon from "@mui/icons-material/Close";
import ShopLayout1 from "components/layouts/ShopLayout1";
import { H1, H5 } from "components/Typography";
import { useAppDispatch } from "hooks/use-app-dispatch";
import modules from "modules";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import BazarButton from "components/BazarButton";
import { useAppSelector } from "hooks/use-app-selector";
import { TopbarFilter } from "components/blog-cards/TopbarFilter";
import { blogFilters, initialPayload } from "modules/blogs";
import { FlexBetween } from "components/flex-box";
import BlogsFilterDrawer from "./BlogsFilterDrawer";
import { useRouter } from "next/router";
import { BlogsPayload } from "modules/blogs/types";
import LazyImage from "components/LazyImage";
import useWindowSize from "hooks/useWindowSize";
import { blogsFlag } from "common/constants/feature-toggles";
import PageDisableComponent from "components/PageDisabled";

interface Props {}

const { selectors: blogSelectors, actions: blogActions } = modules.blogs;

const MainBlogPage: FC<Props> = ({}) => {
  const dispatch = useAppDispatch();
  const width = useWindowSize();
  const isMobile = width <= 1200;
  const router = useRouter();
  const { replace } = useRouter();
  const queries = router?.query as BlogsPayload | undefined;

  const [isBlogFilterOpen, setIsBlogFilterOpen] = useState<boolean>(false);

  const blogs = useAppSelector(blogSelectors.selectBlogList);
  const filters = useAppSelector(blogSelectors.selectFilters);
  const found = useAppSelector(blogSelectors.selectFound);
  const isLoading = useAppSelector(blogSelectors.selectIsBlogsLoading);
  const dateFiltersVal = useAppSelector(blogSelectors.selectDateFilters);
  const contentTypeFiltersVal = useAppSelector(
    blogSelectors.selectContentFilters
  );
  const categoryFiltersVal = useAppSelector(
    blogSelectors.selectCategoryFilters
  );

  const dateFilter = useMemo(
    () => filters?.find((i) => i?.label === blogFilters.date),
    [filters]
  );

  const categoryFilter = useMemo(
    // band-aid solution for category label based on api requirements
    () => filters?.find((i) => i?.label === "category"),
    [filters]
  );

  const contentFilter = useMemo(
    () => filters?.find((i) => i?.label === blogFilters.contentType),
    [filters]
  );

  const isAllBlogsFetched = useMemo(
    () => blogs?.length >= found,
    [blogs, found]
  );

  const handleSetPayload = (val: BlogsPayload) => {
    dispatch(blogActions.setPayload(val));
  };

  const isBlogHasData = useMemo(() => (filters?.[0] ? true : false), [filters]);

  const onReset = useCallback(
    () => {
      let updatedQueries = queries;
      delete updatedQueries[blogFilters.category];
      delete updatedQueries[blogFilters.date];
      delete updatedQueries[blogFilters.contentType];
      replace({ query: updatedQueries }, undefined, { shallow: true });
      dispatch(blogActions.resetAllFilters());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queries]
  );

  const handleToggleFilters = () => setIsBlogFilterOpen(!isBlogFilterOpen);

  const handleChangeFilters = (val: string[], fieldName: string) => {
    let newVal = val;

    const handleAllSelection = (currentFiltersVal, updatedQueryKey) => {
      if (currentFiltersVal.includes("all") && val.length > 1) {
        newVal = newVal.filter((i) => i !== "all");
      } else if (val.includes("all") && !currentFiltersVal.includes("all")) {
        newVal = ["all"];
      } else if (!newVal[0]) {
        newVal = ["all"];
      }

      if (newVal[0] === "all") {
        const updatedQueries = { ...queries };
        delete updatedQueries[updatedQueryKey];
        replace({ query: updatedQueries }, undefined, { shallow: true });
        return updatedQueries;
      } else {
        const updatedQuery = { [updatedQueryKey]: newVal.join(",") };
        replace({ query: { ...queries, ...updatedQuery } }, undefined, {
          shallow: true,
        });
        return updatedQuery;
      }
    };

    switch (fieldName) {
      case blogFilters.category:
        const newCategoryPayload = handleAllSelection(
          categoryFiltersVal,
          fieldName
        );
        if (!newCategoryPayload[fieldName]) {
          dispatch(blogActions.deleteKeyFromPayload(blogFilters.category));
        }
        dispatch(blogActions.setCategoryFilters(newVal));
        handleSetPayload(newCategoryPayload);
        break;

      case blogFilters.date:
        const newDatePayload = handleAllSelection(dateFiltersVal, fieldName);
        if (!newDatePayload[fieldName]) {
          dispatch(blogActions.deleteKeyFromPayload(blogFilters.date));
        }
        dispatch(blogActions.setDateFilters(newVal));
        handleSetPayload(newDatePayload);
        break;

      case blogFilters.contentType:
        const newContentPayload = handleAllSelection(
          contentTypeFiltersVal,
          fieldName
        );
        if (!newContentPayload[fieldName]) {
          dispatch(blogActions.deleteKeyFromPayload(blogFilters.contentType));
        }
        dispatch(blogActions.setContentFilters(newVal));
        handleSetPayload(newContentPayload);
        break;
    }
  };

  const handleGetFilters = async () => {
    // band-aid keyword for category
    await dispatch(blogActions.getBlogFiltersThunk({ keyword: "category" }));
    await dispatch(
      blogActions.getBlogFiltersThunk({ keyword: blogFilters.date })
    );
    await dispatch(
      blogActions.getBlogFiltersThunk({ keyword: blogFilters.contentType })
    );
  };

  const getBlogs = useCallback(async () => {
    await dispatch(blogActions.getBlogsThunk());
  }, [dispatch]);

  const getMoreBlogs = useCallback(async () => {
    await dispatch(blogActions.getBlogsNextPageThunk());
  }, [dispatch]);

  useEffect(() => {
    handleSetPayload(initialPayload);
    handleGetFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (router.isReady) {
      const keys = Object.keys(queries);
      if (keys[0]) {
        keys.forEach((i) => {
          switch (i) {
            case blogFilters.category:
              dispatch(blogActions.setCategoryFilters(queries[i].split(",")));
              dispatch(blogActions.setPayload({ blog_category: queries[i] }));
              break;
            case blogFilters.date:
              dispatch(blogActions.setDateFilters(queries[i].split(",")));
              dispatch(blogActions.setPayload({ year_published: queries[i] }));
              break;
            case blogFilters.contentType:
              dispatch(blogActions.setContentFilters(queries[i].split(",")));
              dispatch(blogActions.setPayload({ content_type: queries[i] }));
              break;
          }
        });
      } else {
        dispatch(blogActions.resetAllFilters());
      }
      getBlogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queries]);

  if (!blogsFlag) {
    return <PageDisableComponent />;
  }

  if (isMobile) {
    return (
      <ShopLayout1 title="Blog ni Kachi">
        <Container sx={{ mt: "2em", mb: "5em" }}>
          <FlexBetween mx="1em">
            <H5
              sx={{ fontWeight: "bold", fontSize: "20px", fontFamily: "Inter" }}
            >
              Our Blogs
            </H5>
            <Button
              sx={{ fontWeight: "bold", fontFamily: "Open Sans" }}
              startIcon={
                <LazyImage
                  width="20px"
                  height="13px"
                  src={"/assets/images/badges/filters.png"}
                />
              }
              onClick={handleToggleFilters}
            >
              Filter
            </Button>
          </FlexBetween>
          <Grid container>
            {blogs?.[0] &&
              blogs.map((i, ind) => (
                <Grid item key={ind} md={4} sm={4} xs={12}>
                  <BlogArticleCard blogData={i} isMobile={isMobile} />
                </Grid>
              ))}
          </Grid>
          <BlogsFilterDrawer
            isLoading={isLoading}
            open={isBlogFilterOpen}
            onReset={onReset}
            toggleOpen={handleToggleFilters}
            handleSetPayload={handleSetPayload}
            dateFilter={dateFilter}
            categoryFilter={categoryFilter}
            contentFilter={contentFilter}
            queries={queries}
          />
          <BazarButton
            disabled={isAllBlogsFetched || isLoading}
            onClick={getMoreBlogs}
            color="secondary"
            variant="contained"
            sx={{
              mb: 1,
              width: "100%",
              height: 40,
              top: 30,
            }}
            startIcon={<ArrowDownwardIcon />}
          >
            <Box display="flex" alignItems="center" justifyContent="center">
              <H5 ml={1}>{isAllBlogsFetched ? "End of Page" : "Load More"}</H5>
            </Box>
          </BazarButton>
        </Container>
      </ShopLayout1>
    );
  }

  return (
    <ShopLayout1 title="Blog ni Kachi">
      <Container
        sx={{
          textAlign: "center",
          fontFamily: "inter",
          paddingTop: "1.2em",
          fontSize: "1em",
          mb: "3em",
        }}
      >
        <H1 sx={{ mb: ".2em", fontSize: "4em" }}>Blog ni Kachi</H1>
        <Typography sx={{ mb: "1em", fontSize: "1.3em" }}>
          The latest industry news, interviews, technologies, and resources.
        </Typography>
        <Grid
          container
          sx={{
            alignItems: "end",
            justifyContent: "space-between",
            width: "50%",
            margin: "0 auto",
          }}
        >
          <TopbarFilter
            disabled={isLoading}
            value={contentTypeFiltersVal}
            onChange={handleChangeFilters}
            filter={contentFilter}
            fieldName={blogFilters.contentType}
            title="Content Type"
          />
          <TopbarFilter
            disabled={isLoading}
            value={categoryFiltersVal}
            onChange={handleChangeFilters}
            fieldName={blogFilters.category}
            filter={categoryFilter}
            title="Category"
          />
          <TopbarFilter
            disabled={isLoading}
            value={dateFiltersVal}
            onChange={handleChangeFilters}
            fieldName={blogFilters.date}
            filter={dateFilter}
            title="Year"
          />
          {isBlogHasData && (
            <Grid
              item
              lg={2}
              md={2}
              sx={{
                paddingLeft: "0 !important",
              }}
            >
              <Button
                disableRipple
                disabled={isLoading}
                startIcon={<CloseIcon sx={{ color: "#C1CDD7" }} />}
                onClick={onReset}
                sx={{
                  fontSize: "12px",
                  "&:hover": {
                    background: "transparent",
                  },
                }}
              >
                Clear All
              </Button>
            </Grid>
          )}
        </Grid>
        <Grid container spacing={3} columns={12} mt={"10px"}>
          {blogs?.[0] &&
            blogs.map(
              (i, ind) =>
                i.published === "Active" && (
                  <Grid item lg={4} key={ind}>
                    <BlogArticleCard blogData={i} />
                  </Grid>
                )
            )}
        </Grid>

        <BazarButton
          disabled={isAllBlogsFetched || isLoading}
          onClick={getMoreBlogs}
          color="secondary"
          variant="contained"
          sx={{ mb: 4.5, width: "100%", height: 40, top: 30, bottom: 30 }}
          startIcon={<ArrowDownwardIcon />}
        >
          <Box display="flex" alignItems="center" justifyContent="center">
            <H5 ml={1}>{isAllBlogsFetched ? "End of Page" : "Load More"}</H5>
          </Box>
        </BazarButton>
      </Container>
    </ShopLayout1>
  );
};

export default MainBlogPage;
